import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material'
import React, { FC, useState } from 'react'

import { getPromptAnswer } from '../../components/Inspections/getPromptAnswer'
import { LoadingWrapper } from '../../components/SharedUI/LoadingWrapper'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { EntryWithRelated, PromptWithRelated } from '../../models/inspections'
import {
  useUpdateInspectionEntryMutation,
  useUpdateInspectionPromptMutation
} from '../../redux/api/inspectionApi'
import { dedupArray } from '../../utils/dedupArray/dedupArray'
import { AnswerRelatedChoice } from './AnswerRelatedChoice'
import { SelectedAnswerWrapper } from './SelectedAnswerWrapper'

interface MultipleChoiceMultipleSelectProps {
  prompt: PromptWithRelated
  entry: EntryWithRelated
}
export const PromptInputForMultiChoiceMultiSelect: FC<
  MultipleChoiceMultipleSelectProps
> = ({ prompt, entry }) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updatePrompt] = useUpdateInspectionPromptMutation()
  const [updateEntry] = useUpdateInspectionEntryMutation()

  const selected = getPromptAnswer(prompt.info!) as string[]
  const onSubmitForMultipleChoiceMultipleSelect = async (value: string[]) => {
    setIsSubmitting(true)
    const promises = []
    promises.push(
      updatePrompt({
        authorization: accessToken,
        promptEntry: {
          ...prompt,
          info: {
            ...prompt.info!,
            answer: undefined,
            multipleChoiceAnswer: undefined,
            multipleChoiceMultipleSelectAnswers:
              value.length === 0 ? undefined : value
          }
        }
      })
    )
    const answers = prompt.answers.filter((answer) =>
      value.includes(answer.id!)
    )
    const customerNotesArr: string[] = []
    let customStatus = entry.info!.customStatus
    answers.forEach((answer) => {
      if (answer.info!.customerNotes) {
        customerNotesArr.push(answer.info!.customerNotes)
      }
      // TODO: define the logic for customStatus if there are multiple answers with different statuses
      if (answer.info!.status) {
        customStatus = answer.info!.status
      }
    })
    promises.push(
      updateEntry({
        authorization: accessToken,
        entryEntry: {
          ...entry,
          info: {
            ...entry.info!,
            customStatus,
            customerNotes: customerNotesArr.join('\n')
          }
        }
      })
    )
    await Promise.all(promises)
    setIsSubmitting(false)
  }

  return (
    <LoadingWrapper loading={isSubmitting}>
      <Typography variant="subtitle1">
        {(prompt.info?.required ? '* ' : '') + prompt.info!.question!}
      </Typography>
      <FormGroup>
        {prompt.answers.map((answer) => {
          const isSelected = selected.includes(answer.id!)
          return (
            <SelectedAnswerWrapper selected={isSelected} key={answer.id!}>
              <FormControlLabel
                key={answer.id}
                value={answer.id}
                control={
                  <Checkbox
                    checked={selected.includes(answer.id!)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      if (event.target.checked) {
                        onSubmitForMultipleChoiceMultipleSelect(
                          dedupArray([...selected, answer.id!])
                        )
                      } else {
                        onSubmitForMultipleChoiceMultipleSelect(
                          selected.filter((v) => v !== answer.id)
                        )
                      }
                    }}
                  />
                }
                label={answer.info?.question}
              />
              <AnswerRelatedChoice
                {...{ answer, prompt, isSelected, setIsSubmitting }}
              />
            </SelectedAnswerWrapper>
          )
        })}
      </FormGroup>
    </LoadingWrapper>
  )
}
